import { Store } from "fw-state";
import { StatusAggregationItem, NestedAggregationItem } from "models/aggregations";
import { LocalStorageCache } from "caching";
import { StartAction } from "../actions";
import { AccountType, AtsOrganizationFilter, FilterKeys } from "models/ats-organization";

type SavedGridState<Filter> = {
    orgFilter: AtsOrganizationFilter;
    filter: Filter;
    filterCounter: number;
    search: string;
    sort: string;
    pageNumber: number;
    pageSize: number;
    previousPageToken: string;
    nextPageToken: string;
    currentPreviousPageToken: string;
    currentNextPageToken: string;
};

type GridShape<List, Filter> = {
    currentUserId: string;

    loaded: boolean;
    collapsedLoaded: boolean;
    errorLoading: boolean;
    errorMessage: string;
    list: List[];
    statusCounts: StatusAggregationItem[];
    dateBuckets: NestedAggregationItem[];
    total: number;
    orgFilter: AtsOrganizationFilter;
    filter: Filter;
    filterCounter: number;
    selectedFilter: any;
    search: string;
    sort: string;
    runDateAggInterval: string;

    previousPageToken: string;
    nextPageToken: string;
    currentPreviousPageToken: string;
    currentNextPageToken: string;
    pageSize: number;
    pageNumber: number;

    collapsedItem: string;
    updatedDate: Date;
};

const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_PAGE_NUMBER = 1;

export class NextPageAction {}
export class PreviousPageAction {}
export class CollapseItemAction {
    constructor(public id: string) {}
}

export class ResetAction {}
export class RefreshAction {}
export class setDefaultStateAction {}
export class FetchListAction<Filter> {
    constructor(
        public filter: Filter = null,
        public search: string = null,
        public sort: string = ""
    ) {}
}

export class AtsOrganizationFilterAction {
    constructor(public filter: AtsOrganizationFilter) {}
}

export class SelectedFilterAction {
    constructor(
        public key: number,
        public data: any
    ) {}
}

export class GridStore<List, Filter> extends Store<GridShape<List, Filter>> {
    private readonly keyPostfix;
    private cache: LocalStorageCache = new LocalStorageCache();

    constructor(keyPostfix) {
        super();
        this.keyPostfix = keyPostfix;
    }

    defaultState() {
        return {
            currentUserId: null,

            loaded: false,
            collapsedLoaded: false,
            errorLoading: false,
            errorMessage: "",
            list: [],
            statusCounts: [],
            dateBuckets: [],
            total: 0,
            orgFilter: { type: AccountType.Customer, mapping: null, tags: null },
            filter: { type: AccountType.Customer } as Filter,
            filterCounter: 0,
            selectedFilter: { [FilterKeys.selectedTypes]: AccountType.Customer },
            search: null,
            sort: null,
            runDateAggInterval: "1d",
            previousPageToken: null,
            nextPageToken: null,
            currentPreviousPageToken: null,
            currentNextPageToken: null,
            pageSize: DEFAULT_PAGE_SIZE,
            pageNumber: DEFAULT_PAGE_NUMBER,

            collapsedItem: "",
            updatedDate: new Date()
        };
    }

    get cacheKey(): string {
        return `manager-user:${this.state.currentUserId}:${this.keyPostfix}`;
    }

    public handleStartAction(action: StartAction): void {
        this.setState(() => ({
            ...this.defaultState(),
            currentUserId: action.user.Id
        }));
    }

    public handleLogoutAction(): void {
        this.setState(() => this.defaultState());
    }

    public handleSelectedFilter(action: SelectedFilterAction) {
        this.setState((state) => ({
            ...state,
            selectedFilter: {
                ...state.selectedFilter,
                [action.key]: action.data
            }
        }));
    }

    public handleSetDefaultStateAction(userId: string): void {
        this.setState(() => ({
            ...this.defaultState(),
            currentUserId: userId
        }));
    }

    public handleFetchList(action: FetchListAction<Filter>, callback = null): Function {
        this.setState(() => ({
            ...this.state,
            filter: action.filter,
            search: action.search,
            sort: action.sort,
            loaded: false,
            collapsedLoaded: false,
            collapsedItem: "",
            errorLoading: false,
            errorMessage: "",
            pageNumber: DEFAULT_PAGE_NUMBER,
            previousPageToken: null,
            nextPageToken: null,
            currentPreviousPageToken: null,
            currentNextPageToken: null
        }));

        this.saveState();
        if (callback) return callback();
    }

    public handleCollapseItemAction(action: CollapseItemAction, callback = null): Function {
        this.setState((state) => ({
            ...state,
            collapsedItem: action.id,
            collapsedLoaded: false
        }));

        if (callback) return callback();
    }

    public handleResetAction(action: ResetAction, callback = null): Function {
        if (!this.state?.loaded) return;

        this.setState(() => {
            return {
                ...this.state,
                orgFilter: { type: AccountType.Customer, mapping: null, tags: null },
                filter: {} as Filter,
                selectedFilter: {}
            };
        });

        this.saveState();

        if (callback) return callback();
    }

    public handleOrgFilterAction(action: AtsOrganizationFilterAction, callback = null): Function {
        if (!this.state?.loaded) return;

        this.setState((state) => ({
            ...state,
            orgFilter: action.filter
        }));

        this.saveState();

        if (callback) return callback();
    }

    public handleRefreshAction(action: RefreshAction, callback = null): Function {
        if (!this.state?.loaded) return;

        this.setState((state) => ({
            ...state,
            loaded: false
        }));

        this.saveState();

        if (callback) return callback();
    }

    public async handleNextPageAction(action: NextPageAction, callback): Promise<Function> {
        const { nextPageToken, pageSize, pageNumber, total } = this.state;

        if (pageNumber * pageSize > total) return;

        this.setState((state) => ({
            ...state,
            loaded: false,
            collapsedItem: "",
            pageNumber: pageNumber + 1
        }));

        return callback(nextPageToken);
    }

    public async handlePreviousPage(action: PreviousPageAction, callback): Promise<Function> {
        const { previousPageToken, pageNumber } = this.state;
        if (pageNumber <= 1) return;

        this.setState((state) => ({
            ...state,
            loaded: false,
            collapsedItem: "",
            pageNumber: pageNumber - 1
        }));

        return callback(previousPageToken);
    }

    public saveState(): void {
        if (this.state.currentUserId == null) return;

        const { list, loaded, statusCounts, total, ...rest } = this.state;

        this.cache?.set<SavedGridState<Filter>>(this.cacheKey, { ...rest });
    }

    public restoreState(): void {
        if (this.state.currentUserId == null) return;

        const savedState = this.cache?.get<SavedGridState<Filter>>(this.cacheKey);

        if (savedState) {
            this.setState((state) => ({
                ...state,
                ...savedState
            }));
        }
    }

    public getWholeFilter(withStatus = true, mapping: Map<string, string> = new Map()): string {
        const { filter } = this.state;

        if (!filter || !Object.keys(filter).length) return null;

        const filterArgs = [];
        for (const [key, value] of Object.entries(filter)) {
            if (value === undefined || value === null) continue;

            if (!withStatus && key === "status") continue;

            if (Array.isArray(value) && value.length) {
                (value as Array<string>).forEach((element) => {
                    filterArgs.push(`${mapping.get(key)}:${element}`);
                });
            } else {
                filterArgs.push(`${mapping.get(key)}:${value}`);
            }
        }

        return filterArgs.join(" AND ");
    }
}
